/**
 * Rive Layer Renderer
 * Renders Rive animations using @rive-app/react-canvas
 */

import React from 'react';
import { useRive, Layout, Fit, Alignment } from '@rive-app/react-canvas';
import type { RiveLayer } from '@/types/layers';
import type { ResolvedPosition } from '@/lib/position/resolver';

interface RiveLayerRendererProps {
  layer: RiveLayer;
  position: ResolvedPosition;
  isSelected?: boolean;
  isHovered?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

// Map our fit/alignment values to Rive's enums
const fitMap: Record<string, Fit> = {
  cover: Fit.Cover,
  contain: Fit.Contain,
  fill: Fit.Fill,
  none: Fit.None,
  scaleDown: Fit.ScaleDown,
};

const alignmentMap: Record<string, Alignment> = {
  center: Alignment.Center,
  topLeft: Alignment.TopLeft,
  topCenter: Alignment.TopCenter,
  topRight: Alignment.TopRight,
  centerLeft: Alignment.CenterLeft,
  centerRight: Alignment.CenterRight,
  bottomLeft: Alignment.BottomLeft,
  bottomCenter: Alignment.BottomCenter,
  bottomRight: Alignment.BottomRight,
};

export function RiveLayerRenderer({
  layer,
  position,
  isSelected,
  isHovered,
  onClick,
  onMouseDown,
  onMouseEnter,
  onMouseLeave,
}: RiveLayerRendererProps) {
  const { content, transform, opacity, width, height } = layer;
  const {
    src,
    stateMachine,
    animations,
    autoplay = true,
    fit = 'contain',
    alignment = 'center',
  } = content;

  const resolvedWidth = position.width ?? (typeof width === 'number' ? width : 200);
  const resolvedHeight = position.height ?? (typeof height === 'number' ? height : 200);

  const { RiveComponent } = useRive({
    src,
    stateMachines: stateMachine ? [stateMachine] : undefined,
    animations: animations,
    autoplay,
    layout: new Layout({
      fit: fitMap[fit] ?? Fit.Contain,
      alignment: alignmentMap[alignment] ?? Alignment.Center,
    }),
  });

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    left: position.x,
    top: position.y,
    width: resolvedWidth,
    height: resolvedHeight,
    opacity,
    transform: transform
      ? `rotate(${transform.rotation ?? 0}deg) scale(${transform.scaleX ?? 1}, ${transform.scaleY ?? 1})`
      : undefined,
    transformOrigin: transform
      ? `${(transform.originX ?? 0.5) * 100}% ${(transform.originY ?? 0.5) * 100}%`
      : undefined,
    // Hover styling (selection handled by SelectionOverlay)
    outline: isHovered && !isSelected ? '1px dashed #3B82F6' : 'none',
    outlineOffset: 2,
    cursor: layer.locked ? 'default' : 'pointer',
    pointerEvents: layer.locked ? 'none' : 'auto',
    overflow: 'hidden',
  };

  if (!src) {
    return (
      <div
        style={{
          ...containerStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255,255,255,0.05)',
          border: '1px dashed rgba(255,255,255,0.2)',
        }}
        onClick={onClick}
        onMouseDown={onMouseDown}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        data-layer-id={layer.id}
      >
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
          No Rive source
        </span>
      </div>
    );
  }

  return (
    <div
      style={containerStyle}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-layer-id={layer.id}
    >
      <RiveComponent
        style={{
          width: '100%',
          height: '100%',
        }}
      />
    </div>
  );
}
